import { ChangeDetectionStrategy, Component, DestroyRef, signal } from '@angular/core';
import { NavigationMenu, NavigationMenuItem, NavigationMenuItems } from '../../const/navigation-menu-items';
import { TabsModule } from 'primeng/tabs';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { OverlayBadge } from 'primeng/overlaybadge';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { filter, Observable } from 'rxjs';
import { CartShortInfo } from '@shared/interfaces/ordering-carts/ordering-carts.interface';
import { CartFacade } from '../../../../store/cart';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe } from '@jsverse/transloco';
import { SkeletonModule } from 'primeng/skeleton';
import { MobileMenuExtraService } from '@services/utils/mobile-menu-extra.service';
import { TissIconComponent } from '@ui/tiss-icon/tiss-icon.component';
import { YandexMetrikaService } from '@services/utils/yandex-metrika.service';

const imports = [TabsModule, SkeletonModule, RouterLink, OverlayBadge, AsyncPipe, TranslocoPipe, NgTemplateOutlet];

@Component({
  selector: 'b2b-navigation-mobile-menu',
  imports: [...imports, TissIconComponent],
  templateUrl: './navigation-mobile-menu.component.html',
  styleUrl: './navigation-mobile-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationMobileMenuComponent {
  navigationMenuItems: NavigationMenuItem[];
  userCartShortInfo$: Observable<CartShortInfo | null> = this.cartFacade.cartShortInfo$;

  activeTabValue = signal<NavigationMenu | ''>('');

  readonly extensionBlock = this.mobileMenuExtraService.template;
  readonly hideTabsWhenExtension = this.mobileMenuExtraService.hideTabs;

  protected readonly NavigationMenu = NavigationMenu;

  constructor(
    private cartFacade: CartFacade,
    private router: Router,
    private destroyRef: DestroyRef,
    private mobileMenuExtraService: MobileMenuExtraService,
    yandexMetrikaService: YandexMetrikaService,
  ) {
    const abTestParams = yandexMetrikaService.getAbTestCartQueryParams();
    this.navigationMenuItems = NavigationMenuItems.map(item =>
      item.value === NavigationMenu.cart ? { ...item, queryParams: abTestParams } : item,
    );
    this.initializeRouteListener();
  }

  private initializeRouteListener(): void {
    this.updateActiveTab(this.router.url);

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((navigationEndEvent: NavigationEnd) => {
        this.updateActiveTab(navigationEndEvent.urlAfterRedirects);
      });
  }

  private updateActiveTab(url: string): void {
    const matched = this.findMatchingMenuItem(url);
    const activeTab = matched ? matched.value : '';
    this.activeTabValue.set(activeTab);
  }

  private findMatchingMenuItem(url: string): NavigationMenuItem | undefined {
    return this.navigationMenuItems.find(item => this.routePattern(item.route).test(url));
  }

  // Шаблон для проверки: точное совпадение маршрута или любой его вложенный путь (например, `/user-profile` и `/user-profile/personal-information`)
  private routePattern(route: string) {
    return new RegExp(`^${route}(?:/.*)?$`);
  }
}
